import { useState } from "react";
import type { ClaimType } from "../types";
import { Button } from "#/components/common/Button";

const claimTypes: ClaimType[] = [
  "TRAVEL",
  "MEAL",
  "MEDICAL",
  "ACCOMMODATION",
  "OFFICE_SUPPLIES",
  "TRAINING",
  "OTHER",
];

type Values = {
  claimType: ClaimType;
  purpose: string;
};

type Props = {
  defaultValues?: Partial<Values>;
  loading?: boolean;
  onSubmit: (values: Values) => Promise<void> | void;
};

export function ClaimDraftForm({ defaultValues, loading, onSubmit }: Props) {
  const [claimType, setClaimType] = useState<ClaimType>(
    defaultValues?.claimType ?? "TRAVEL",
  );
  const [purpose, setPurpose] = useState(defaultValues?.purpose ?? "");
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");

    if (!purpose.trim()) {
      setError("Purpose is required.");
      return;
    }

    await onSubmit({
      claimType,
      purpose: purpose.trim(),
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Claim details</h2>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-semibold uppercase text-slate-500">
            Claim type
          </label>
          <select
            value={claimType}
            onChange={(e) => setClaimType(e.target.value as ClaimType)}
            className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          >
            {claimTypes.map((type) => (
              <option key={type} value={type}>
                {type.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase text-slate-500">
            Purpose
          </label>
          <input
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. Travel reimbursement for field visit"
            className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" loading={loading}>
          Save draft
        </Button>
      </div>
    </form>
  );
}